import { createContext, useContext, useMemo } from 'react';

// THEME TYPES
export interface ThemeColors {
  bg: string;
  bgGradient: string;
  sidebar: string;
  card: string;
  cardAlt: string;
  text: string;
  textSecondary: string;
  border: string;
  gridLine: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  titleColor: string;
  inputBg: string;
  inputBorder: string;
  tableRowHover: string;
  tableHeader: string;
}

export interface ThemeContextType {
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
  toggleDarkMode: () => void;
  theme: ThemeColors;
  isMobile: boolean;
  isTablet: boolean;
  windowWidth: number;
  sidebarPosition: 'side' | 'top';
}

// THEME CONTEXT
export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// THEME HOOK

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within AdminLayout');
  }
  return context;
};

// THEME GENERATOR

export const generateTheme = (darkMode: boolean): ThemeColors => ({
  bg: darkMode ? '#0f172a' : '#1e90ff',
  bgGradient: darkMode
    ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
    : 'linear-gradient(180deg, #1e90ff 0%, #00bfff 100%)',
  sidebar: darkMode ? '#020617' : '#3b82f6',
  card: darkMode ? '#1e293b' : '#fff',
  cardAlt: darkMode ? '#0f172a' : '#0f172a',
  text: darkMode ? '#f1f5f9' : '#111827',
  textSecondary: darkMode ? '#94a3b8' : '#6b7280',
  border: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
  gridLine: darkMode ? '#334155' : '#f0f0f0',
  tooltipBg: darkMode ? '#334155' : '#fff',
  tooltipBorder: darkMode ? '#475569' : '#e5e7eb',
  tooltipText: darkMode ? '#f1f5f9' : '#374151',
  titleColor: darkMode ? '#f1f5f9' : '#0a1628',
  inputBg: darkMode ? '#0f172a' : '#fff',
  inputBorder: darkMode ? '#475569' : '#d1d5db',
  tableRowHover: darkMode ? '#334155' : '#f3f4f6',
  tableHeader: darkMode ? '#0f172a' : '#f9fafb',
});
